/* eslint-disable no-nested-ternary */
import type { UseFormSetValue } from 'react-hook-form';
import { isArray } from 'lodash';
import {
  checkDTPricingCompatible,
  checkIfMTO,
  MTO_MOQ,
  getCartPricingReqPayload,
  getDesignGroupQuantity,
  isDesignBlockedForCheckout,
  resolveEffectiveMtoMoq,
  resolveQuotedCartUnitPrice,
  DEFAULT_MOQ,
} from './PricingUtils';
import { isDesignRejected } from './LicensingUtils';
import {
  hasMoreThanThreeColorsColorsQuoterPayload,
  resolveQuotableShipType,
} from './ShippingUtils';
import type {
  ShipType,
  GetQuoteMutationFunction,
  IQuantitySizeItem,
} from '@/common/interfaces/order.interface';
import {
  isStoreCartLineActive,
  resolveStoreCartItemSelectionFlag,
} from '@/modules/Cart/StoreCart/helpers/storeCartApiSync.utils';
import { getStoreCartDesignGroupKey } from '@/modules/Cart/StoreCart/helpers/storeCartDesignGroups.utils';
import { readPersistedStoreCartDesignGroupMinimums } from '@/modules/Cart/StoreCart/helpers/storeCartNavigation.utils';
import {
  getStoreCartBaseUnitPrice,
  getStoreCartDrawerDefaultMinQuantity,
  getStoreCartPriceFromPriceListOnly,
} from '@/modules/Cart/StoreCart/helpers/storeCartPricing.utils';
import type {
  ICartCountResponse,
  ICartForm,
  ICartInformation,
  ICartItemDetail,
  ICartItemPayload,
  ICartResponse,
  ICartDesignDisplayGroup,
} from '@/common/interfaces/cart.interface';
import type {
  IDesignItem,
  IDesignObj,
  IDesignProofItem,
} from '@/common/interfaces/design.interface';
import { LICENSING_LABEL_DISABLE_CART } from '@/constants/LicensingStatus';
import { captureCartViewed } from '@/mixpanel/MixpanelEvents';
import {
  consumeDesignDefaultSizePrefill,
  readCartItemSizeQuantities,
  writeCartItemSizeQuantities,
} from '@/utilities/cartSizeQuantityStorage';
import { getCartItemApparelSizesFromCookie } from '@/utilities/CartSizeQuantityCookieUtils';
import type {
  IAdditionalShippingPriceRes,
  IQuoteBreakpoints,
  IQuoteResponse,
} from '@/common/interfaces/quoter.interface';
import { getOnlyNumbers } from '@/components/utils/FunctionUtils';
import {
  DISABLED_CART_TOOLTIP_LICENSING,
  DISABLED_STORE_TOOLTIP_LICENSING,
} from '@/constants/Order';
import CartButtonToolTip from '@/common/ToolTips/CartButtonToolTip';
import StoreButtonToolTip from '@/common/ToolTips/StoreButtonToolTip';

export const MOCK_PRICE_LIST: { quantity: number; unitPrice: number }[] = [
  {
    quantity: 12,
    unitPrice: 90,
  },
  {
    quantity: 24,
    unitPrice: 80,
  },
  {
    quantity: 36,
    unitPrice: 70,
  },
  {
    quantity: 48,
    unitPrice: 60,
  },
  {
    quantity: 60,
    unitPrice: 50,
  },
  {
    quantity: 72,
    unitPrice: 40,
  },
  {
    quantity: 100,
    unitPrice: 30,
  },
  {
    quantity: 144,
    unitPrice: 20,
  },
  {
    quantity: 150,
    unitPrice: 10,
  },
  {
    quantity: 288,
    unitPrice: 5,
  },
];

/**
 * Function to map designItem to cart payload
 * @param design - data to use
 * @param isStore - boolean to check if store or cart item
 * @param unitPrice - unitPrice to send to DB
 * @param quantity - Quantity to send to DB
 */
export const mapDesignItemToCartPayload = (
  design: IDesignItem,
  isStore = false,
  unitPrice = 0,
  quantity = 0
): ICartItemPayload => {
  const designId = design.design.id as number;
  const proofId = getOnlyNumbers(design.proof_item_id);
  const apparelColor = design.apparel?.color ? design.apparel?.color : '';

  let isMadeToOrder = false;

  const decorMethodInfo = design?.design?.decorMethodInfo || [];

  if (isArray(decorMethodInfo)) {
    isMadeToOrder = checkIfMTO(decorMethodInfo);
  }

  if (!isStore && !quantity) {
    quantity = isMadeToOrder ? MTO_MOQ : DEFAULT_MOQ;
  }

  return {
    proofId,
    designId,
    isStore,
    apparelColor,
    isMadeToOrder,
    price: unitPrice || 0,
    apparelStyleCode: design?.apparel?.styleCode ? design.apparel.styleCode : '',
    apparelName: (design.apparel ? design.apparel.name : '') as string,
    apparelBrand: (design.apparel ? design.apparel.brand : '') as string,
    apparelHex: (design.apparel ? design.apparel.hexCode : '') as string,
    /** Store cart creates send 0; minimum is chosen later in the store cart. */
    quantity: isStore ? 0 : quantity,
    isFlashEligible: (design.apparel ? design.apparel.isFlashEligible : false) as boolean,
    alphaNumericProofItemId: design?.proof_item_id,
    proofItemVersionName: design?.name,
    hasCustomNames: (design.design ? design.design.hasCustomNames : false) as boolean,
    hasCustomNumbers: (design.design ? design.design.hasCustomNumbers : false) as boolean,
    decorMethodInfo: !!design.design.decorMethodInfo,
  };
};

/**
 * Internal helper to build design group cart payload with configurable price resolution
 * @param design - design item data
 * @param isStore - boolean to check if store or cart item
 * @param priceResolver - function to resolve price for a given key and default price
 * @param quantities - object with quantities per key
 */
// eslint-disable-next-line no-underscore-dangle
const _buildDesignGroupCartPayload = (
  design: IDesignItem,
  isStore = false,
  unitPrice = 0,
  quantities?: { [key: string]: string },
  unitPrices?: { [key: string]: number }
): ICartItemPayload[] => {
  const transformedDesigns = design.designGroupDesigns?.transformedDesigns || [];

  if (!transformedDesigns.length) {
    // For single designs, use the first quantity or default
    const defaultQuantity = quantities ? Object.values(quantities)[0] || '12' : '12';
    const defaultPrice = unitPrice;
    return [mapDesignItemToCartPayload(design, isStore, defaultPrice, Number(defaultQuantity))];
  }

  const cartPayloads: ICartItemPayload[] = [];

  transformedDesigns.forEach((transformedDesign) => {
    // For each transformed design, create cart payloads for each color
    transformedDesign.colors.forEach((color) => {
      const { designId } = color;
      const proofId = getOnlyNumbers(design.proof_item_id);
      const apparelColor = color.colorName;

      let isMadeToOrder = false;

      // decorMethodInfo comes from transformedDesigns decorMethodInfo
      const decorMethodInfo = transformedDesign.decorMethodInfo || [];

      if (isArray(decorMethodInfo)) {
        isMadeToOrder = checkIfMTO(decorMethodInfo);
      }

      // isFlashEligible comes from transformedDesigns isFlashEligible
      const isFlashEligible = transformedDesign.isFlashEligible || false;

      // Get the quantity for this specific product-color combination
      const key = `${transformedDesign.styleCode}-${color.colorName}-${designId}`;
      let itemQuantity = quantities?.[key] ? Number(quantities[key]) : 0;
      const itemUnitPrice = unitPrices?.[key] ?? unitPrice;

      if (!isStore && !itemQuantity) {
        itemQuantity = isMadeToOrder ? MTO_MOQ : DEFAULT_MOQ;
      }

      cartPayloads.push({
        proofId,
        designId,
        isStore,
        apparelColor,
        isMadeToOrder,
        price: itemUnitPrice || 0,
        apparelStyleCode: transformedDesign.styleCode || '',
        apparelName: transformedDesign.apparelData?.name || '',
        apparelBrand: transformedDesign.apparelData?.brand || '',
        apparelHex: color.colorHex || '',
        /** Store cart creates send 0; minimum is chosen later in the store cart. */
        quantity: isStore ? 0 : itemQuantity,
        isFlashEligible,
        alphaNumericProofItemId: design?.proof_item_id,
        proofItemVersionName: design?.name,
        hasCustomNames: (design.design ? design.design.hasCustomNames : false) as boolean,
        hasCustomNumbers: (design.design ? design.design.hasCustomNumbers : false) as boolean,
        decorMethodInfo: !!transformedDesign.decorMethodInfo,
      });
    });
  });

  return cartPayloads;
};

export const getDesignGroupCartPayload = (
  design: IDesignItem,
  isStore = false,
  unitPrice = 0,
  quantities?: { [key: string]: string },
  unitPrices?: { [key: string]: number }
): ICartItemPayload[] =>
  _buildDesignGroupCartPayload(design, isStore, unitPrice, quantities, unitPrices);

/**
 * Function to get design group cart payload with prices per product-color combination
 * @param design - design item data
 * @param isStore - boolean to check if store or cart item
 * @param prices - object with prices per key (product-color combination)
 * @param quantities - object with quantities per key
 */
export const getDesignGroupCartPayloadWithPrices = (
  design: IDesignItem,
  isStore = false,
  prices: { [key: string]: number } = {},
  quantities?: { [key: string]: string }
): ICartItemPayload[] => _buildDesignGroupCartPayload(design, isStore, 0, quantities, prices);

/** To get the minimum order value for order type
 *  @param _isFlashEligible - retained for callers; flash currently uses the same MOQ as standard (12)
 *  @param isMTOOrder - boolean check for mto order
 *
 */
export const getCartMinOrderQuantity = (_isFlashEligible: boolean, isMTOOrder: boolean): number => {
  if (isMTOOrder) {
    return 50;
  }

  return 12;
};

/** Default qty for the first size when an item is newly added to cart. */
export const getDefaultFirstSizePrefillQuantity = (
  isFlashEligible: boolean,
  isMadeToOrder: boolean
): number => {
  const minQ = getCartMinOrderQuantity(isFlashEligible, isMadeToOrder);
  return minQ > 0 ? minQ : 1;
};

/** Parse size label → numeric code from `apparelSizes` (supports `code:size` and `code:size:qty`). */
export const parseApparelSizeCodes = (apparelSizes: string): Record<string, number> => {
  const codes: Record<string, number> = {};
  if (!apparelSizes?.trim()) return codes;

  apparelSizes.split(';').forEach((entry) => {
    const trimmed = entry.trim();
    if (!trimmed) return;

    const [codePart, sizePart] = trimmed.split(':');
    const size = sizePart?.trim();
    if (!size) return;

    const code = parseInt(codePart, 10);
    if (!Number.isNaN(code)) {
      codes[size] = code;
    }
  });

  return codes;
};

/**
 * Build `code:size:qty` payload from cart form fields.
 * Emits every known size label (including `:0`) so the full size list survives cart reloads.
 */
export const buildCartItemApparelSizes = (
  item: Pick<ICartItemDetail, 'codes' | 'sizes' | 'apparelSizes'>
): string => {
  const codes = item.codes ?? parseApparelSizeCodes(item.apparelSizes ?? '');
  const sizes = item.sizes ?? {};

  if (!Object.keys(codes).length) {
    return item.apparelSizes ?? '';
  }

  return Object.entries(codes)
    .map(([label, code]) => {
      const rawQty = sizes[label];
      const qty = rawQty === '' || rawQty == null ? 0 : parseInt(String(rawQty), 10) || 0;
      return `${code}:${label}:${qty}`;
    })
    .join(';');
};

/**
 * Parse apparelSizes string into size label → quantity for cart form.
 * Supports stored `code:size:qty` segments; legacy `code:size` uses `initialQuantity` on first label only.
 */
export const parseApparelSizesForCart = (
  apparelSizes: string,
  initialQuantity?: number
): Record<string, string> => {
  const sizes: Record<string, string> = {};
  if (!apparelSizes?.trim()) return sizes;

  let hasThreePartSegments = false;

  apparelSizes.split(';').forEach((entry) => {
    const trimmed = entry.trim();
    if (!trimmed) return;

    const parts = trimmed.split(':');
    const size = parts[1]?.trim();
    if (!size) return;

    if (parts.length >= 3) {
      hasThreePartSegments = true;
      const qty = parseInt(parts[2], 10);
      sizes[size] = !Number.isNaN(qty) && qty > 0 ? String(qty) : '';
      return;
    }

    sizes[size] = '';
  });

  const keys = Object.keys(sizes);
  if (!hasThreePartSegments && keys.length && initialQuantity != null && initialQuantity > 0) {
    sizes[keys[0]] = String(initialQuantity);
  }

  return sizes;
};

function apparelSizesHasThreePartSegments(apparelSizes: string): boolean {
  if (!apparelSizes?.trim()) return false;
  return apparelSizes.split(';').some((entry) => entry.trim().split(':').length >= 3);
}

/**
 * True only when EVERY non-empty segment of `apparelSizes` is a well-formed
 * `code:size:qty` (exactly 3 non-empty fields) entry with a numeric code and a
 * valid, non-negative quantity. Used to gate whether a DT cookie value is
 * trustworthy enough to treat as authoritative — a single malformed segment
 * (e.g. a non-numeric code or quantity) means the whole cookie is
 * corrupted/untrustworthy and must fall back to item.apparelSizes/stored
 * quantities instead of partially or incorrectly overriding them.
 */
function hasWellFormedApparelSizeSegments(apparelSizes: string): boolean {
  if (!apparelSizes?.trim()) {
    return false;
  }

  const segments = apparelSizes
    .split(';')
    .map((entry) => entry.trim())
    .filter(Boolean);

  if (!segments.length) {
    return false;
  }

  return segments.every((entry) => {
    const parts = entry.split(':').map((part) => part.trim());
    if (parts.length !== 3 || parts.some((part) => !part)) {
      return false;
    }

    const code = Number(parts[0]);
    const qty = Number(parts[2]);
    return Number.isFinite(code) && Number.isFinite(qty) && qty >= 0;
  });
}

/**
 * Builds cart form size inputs from API `apparelSizes`, optional stored qty, and size catalog.
 * Never assigns total `quantity` to the first size label for multi-size products.
 *
 * `v3`'s cart-item API doesn't persist DT's per-size quantity breakdown — its
 * `apparelSizes` response is the apparel catalog's available sizes, not the
 * customer's chosen quantities. When the item came from a DT checkout handoff
 * within the cookie's lifetime, prefer that DT-sourced breakdown over the
 * catalog-derived `apparelSizes`/stored quantities (same fix as CheckoutV4's
 * `getCartItemSizeQuantities`). Skipped for store items — never DT-originated.
 */
export const resolveCartItemSizesForForm = (
  item: Pick<ICartItemDetail, 'id' | 'apparelSizes' | 'quantity' | 'sizeQuantities'> &
    Partial<Pick<ICartItemDetail, 'isStore'>>,
  userId?: number | string
): Record<string, string> => {
  const apparelSizesFromCookie = item.isStore
    ? undefined
    : getCartItemApparelSizesFromCookie(item.id);
  // A malformed/corrupted cookie value must never partially or incorrectly override
  // item.apparelSizes — compute authoritativeness first, then choose the source.
  const hasAuthoritativeCookieBreakdown = Boolean(
    apparelSizesFromCookie && hasWellFormedApparelSizeSegments(apparelSizesFromCookie)
  );
  // hasAuthoritativeCookieBreakdown is only true when apparelSizesFromCookie is truthy
  // (see its Boolean(...) check above), so this narrowing is safe.
  const effectiveApparelSizes = hasAuthoritativeCookieBreakdown
    ? (apparelSizesFromCookie as string)
    : item.apparelSizes;

  const catalogCodes = parseApparelSizeCodes(effectiveApparelSizes ?? '');
  const catalogLabels = Object.keys(catalogCodes);

  const applyServerQuantities = (record: Record<string, string>) => {
    Object.entries(item.sizeQuantities ?? {}).forEach(([label, qty]) => {
      if (qty > 0) {
        record[label] = String(qty);
      }
    });
  };

  if (!catalogLabels.length) {
    const noCatalogRecord = parseApparelSizesForCart(effectiveApparelSizes, item.quantity);
    applyServerQuantities(noCatalogRecord);
    return noCatalogRecord;
  }

  const sizesRecord: Record<string, string> = {};
  catalogLabels.forEach((label) => {
    sizesRecord[label] = '';
  });

  if (apparelSizesHasThreePartSegments(effectiveApparelSizes)) {
    Object.entries(parseApparelSizesForCart(effectiveApparelSizes)).forEach(([label, qty]) => {
      if (label in sizesRecord && qty) {
        sizesRecord[label] = qty;
      }
    });
  }

  applyServerQuantities(sizesRecord);

  // A DT cookie breakdown is already the freshest known truth for this item —
  // don't let a potentially-stale local cache override it. A non-authoritative
  // (absent or malformed) cookie falls all the way back to item.apparelSizes, so
  // stored quantities should still apply normally in that case.
  if (!hasAuthoritativeCookieBreakdown) {
    const storedQuantities = readCartItemSizeQuantities(userId, item.id);
    Object.entries(storedQuantities).forEach(([label, qty]) => {
      if (label in sizesRecord) {
        sizesRecord[label] = String(qty);
      }
    });
  }

  return sizesRecord;
};

export function totalQuantityFromSizeFormRecord(sizes: Record<string, string>): number {
  return Object.values(sizes).reduce((sum, rawQty) => sum + (parseInt(rawQty, 10) || 0), 0);
}

/** Signature used to detect when cart API data should re-hydrate the cart form (incl. per-size qty). */
export const getCartFormInitSignature = (items: ICartItemDetail[]): string =>
  items
    .map(
      (item) =>
        `${item.id}:${item.quantity}:${item.apparelSizes ?? ''}:${JSON.stringify(
          item.sizeQuantities ?? null
        )}`
    )
    .sort()
    .join('|');

const MAX_CART_LINE_QUANTITY = 1_000_000;

/**
 * Guards against corrupt API quantities (e.g. unsigned -1 / UINT32_MAX) breaking cart UI totals.
 * Zero is a valid quantity (e.g. all size pills cleared) and must not fall back to MOQ.
 * Non-numeric falsey values that Number() would coerce to 0 (null, '', false, []) are rejected.
 */
export const normalizeCartLineQuantity = (quantity: unknown, fallback: number): number => {
  if (
    quantity === null ||
    quantity === false ||
    (typeof quantity === 'string' && quantity.trim() === '') ||
    (Array.isArray(quantity) && quantity.length === 0)
  ) {
    return fallback;
  }

  const parsed = Number(quantity);

  if (!Number.isFinite(parsed) || parsed < 0 || parsed > MAX_CART_LINE_QUANTITY) {
    return fallback;
  }

  return Math.floor(parsed);
};

const resolveStoreCartItemMinQuant = (item: ICartItemDetail): number => {
  if (!item.isStore) {
    return getCartMinOrderQuantity(item.isFlashEligible, item.isMadeToOrder);
  }

  const drawerDefault = getStoreCartDrawerDefaultMinQuantity(item.priceList);
  const cartQuantity = Number(item.quantity);
  const savedApiMinimum = Number(item.savedMinimumQuantity ?? 0);
  const sessionMinimum = Number(
    readPersistedStoreCartDesignGroupMinimums()[getStoreCartDesignGroupKey(item)] ?? 0
  );

  // After the user updates the minimum (or store GET has minimum_quantity), prefer
  // the persisted cart-item quantity over the first-breakpoint default.
  // Design-group MOQ floors (e.g. 16) are valid even when they are not in the static
  // drawer tier list [12, 24, ...] — do not snap those back to 12 on cart reopen.
  let persistedMinimum = 0;
  if (savedApiMinimum > 0) {
    persistedMinimum = savedApiMinimum;
  } else if (sessionMinimum > 0) {
    persistedMinimum = sessionMinimum;
  } else if (Number.isFinite(cartQuantity) && cartQuantity > 0) {
    persistedMinimum = cartQuantity;
  }

  if (persistedMinimum > 0) {
    return persistedMinimum;
  }

  // Initial add (quantity 0 / unset) — always select the first breakpoint.
  return drawerDefault;
};

/** Initializing the cart form details
 *  @param data - Cart item details payload
 *
 */
const mapRegularCartFormItem = (
  item: ICartItemDetail,
  userOrg?: string,
  userId?: number | string
): ICartItemDetail => {
  const minQ = getCartMinOrderQuantity(item.isFlashEligible, item.isMadeToOrder);
  let sizes = resolveCartItemSizesForForm(item, userId);
  const apparelSizesFromCookie = item.isStore
    ? undefined
    : getCartItemApparelSizesFromCookie(item.id);
  // A DT cookie with a real `code:size:qty` breakdown is authoritative even when every
  // qty is 0 (e.g. the user cleared all size pills before checkout) — that's a genuine
  // "nothing selected" state, not missing data, and must not be overwritten below with
  // `item.quantity` the way a true missing-data placeholder would be. A malformed cookie
  // (e.g. a non-numeric code or qty) must never be treated as authoritative — compute
  // this before choosing effectiveApparelSizes so a corrupted cookie falls all the way
  // back to item.apparelSizes instead of partially/incorrectly overriding it.
  const hasAuthoritativeCookieBreakdown = Boolean(
    apparelSizesFromCookie && hasWellFormedApparelSizeSegments(apparelSizesFromCookie)
  );
  // Same effective source resolveCartItemSizesForForm uses (cookie-preferred when
  // authoritative, skipped for store items) — otherwise a cookie-only size label would
  // resolve into `sizes` above but get silently dropped below when catalogCodes/
  // apparelSizes are rebuilt from the raw, catalog-derived item.apparelSizes instead.
  // hasAuthoritativeCookieBreakdown is only true when apparelSizesFromCookie is truthy
  // (see its Boolean(...) check above), so this narrowing is safe.
  const effectiveApparelSizes = hasAuthoritativeCookieBreakdown
    ? (apparelSizesFromCookie as string)
    : item.apparelSizes;
  const catalogLabels = Object.keys(parseApparelSizeCodes(effectiveApparelSizes ?? ''));
  const hasStoredSizeQuantities =
    Object.keys(readCartItemSizeQuantities(userId, item.id)).length > 0;

  if (
    catalogLabels.length > 0 &&
    !hasStoredSizeQuantities &&
    totalQuantityFromSizeFormRecord(sizes) === 0 &&
    consumeDesignDefaultSizePrefill(userId, item.designId) &&
    // An authoritative all-zero DT cookie breakdown means "nothing selected", not
    // "no data yet" — don't let the newly-added-design prefill fabricate a quantity
    // over it (same guarantee as the sizeTotal/quantity fallback below).
    !hasAuthoritativeCookieBreakdown
  ) {
    // Prefer the proof/cart line quantity when present; otherwise use default MOQ.
    const existingQty = normalizeCartLineQuantity(item.quantity, 0);
    const prefillQty =
      existingQty > 0
        ? existingQty
        : getDefaultFirstSizePrefillQuantity(item.isFlashEligible, item.isMadeToOrder);
    sizes = { ...sizes, [catalogLabels[0]]: String(prefillQty) };
    writeCartItemSizeQuantities(userId, item.id, sizes);
  }

  let sizeTotal = totalQuantityFromSizeFormRecord(sizes);
  let quantity;
  if (sizeTotal > 0) {
    quantity = sizeTotal;
  } else if (hasAuthoritativeCookieBreakdown) {
    quantity = 0;
  } else {
    quantity = normalizeCartLineQuantity(item.quantity, minQ);
  }

  if (hasAuthoritativeCookieBreakdown && sizeTotal === 0) {
    // The cookie authoritatively says every size is 0 — clear any stale positive
    // quantities still cached in localStorage so a later load (e.g. once this
    // cookie expires) doesn't resurrect them instead of this genuine all-zero state.
    writeCartItemSizeQuantities(userId, item.id, {});
  }

  /**
   * Keep pills and line quantity aligned: if catalog sizes exist but none have qty,
   * put the line quantity on the first size (same as default MOQ prefill target).
   * `quantity` is already forced to 0 above when the cookie's all-zero breakdown is
   * authoritative, so this naturally no-ops in that case (quantity > 0 is false).
   */
  if (catalogLabels.length > 0 && sizeTotal === 0 && quantity > 0) {
    sizes = { ...sizes, [catalogLabels[0]]: String(quantity) };
    writeCartItemSizeQuantities(userId, item.id, sizes);
    sizeTotal = quantity;
  }

  const catalogCodes = parseApparelSizeCodes(effectiveApparelSizes ?? '');
  const rebuiltApparelSizes = {
    codes: catalogCodes,
    sizes,
    apparelSizes: effectiveApparelSizes,
  };
  const apparelSizes =
    Object.keys(catalogCodes).length > 0
      ? buildCartItemApparelSizes(rebuiltApparelSizes)
      : item.apparelSizes;

  return {
    apparelBrand: item.apparelBrand,
    apparelColor: item.apparelColor,
    apparelHex: item.apparelHex,
    apparelName: item.apparelName,
    apparelStyleCode: item.apparelStyleCode,
    backImage: item.backImage,
    decoratingMethod: item.decoratingMethod,
    designId: item.designId,
    frontImage: item.frontImage,
    hasCustomNames: item.hasCustomNames,
    hasCustomNumbers: item.hasCustomNumbers,
    id: item.id,
    notes: item.notes,
    shipType: item.shipType,
    isFlashEligible: item.isFlashEligible,
    isMadeToOrder: item.isMadeToOrder,
    isExternalApparel: item.isExternalApparel,
    isStore: item.isStore,
    price: item.price,
    primaryImage: item.primaryImage,
    printTypes: item.printTypes,
    minQuant: minQ,
    individualMoq: item.isMadeToOrder ? minQ : undefined,
    proofId: item.proofId,
    cartItemId: item.id,
    proofItemId: item.proofItemId,
    variantId: item.variantId,
    variantSnapshot: item.variantSnapshot,
    proofTitle: item.proofTitle,
    quantity,
    sizes,
    codes: catalogCodes,
    printTypeVariant: item.printTypeVariant,
    decoratingVariant: item.decoratingVariant,
    designLicensing: item.designLicensing,
    apparelSizes,
    designImgPrimary: item.designImgPrimary,
    designImgSecondary: item.designImgSecondary,
    productDescription: item.productDescription,
    productTitle: item.productTitle,
    priceList: [],
    proofItemVersionName: item.proofItemVersionName,
    alphaNumericProofItemId: item.alphaNumericProofItemId,
    apparelPrice: item.apparelPrice,
    apparelCategory: item.apparelCategory,
    apparelWeight: item.apparelWeight,
    apparelType: item.apparelType,
    apparelColorCode: item.apparelColorCode,
    discount: item.discount,
    printTypeInfo: item.printTypeInfo,
    decorMethodInfo: item.decorMethodInfo,
    fromDesignTool: item.fromDesignTool,
    organizationId: item.organizationId,
    collegiateId: item.collegiateId,
    isStreamlinedProofItem: item.isStreamlinedProofItem,
    strikedPricePerUnit: 0,
    standardShipPrice: 0,
    flashShipPrice: 0,
    expeditedShipPrice: 0,
    economyShipPrice: 0,
    designPricing: item.designPricing,
    designGroupItemId: item.designGroupItemId,
    designRejectionInfo: isDesignRejected(
      item.fromDesignTool,
      item.designLicensing,
      item.designPricing,
      userOrg
    ),
    outOfStock: item.outOfStock ?? false,
    isActive: isStoreCartLineActive(item),
    is_selected: resolveStoreCartItemSelectionFlag(item) ?? item.is_selected,
  };
};

const mapStoreCartFormItem = (item: ICartItemDetail, userOrg?: string): ICartItemDetail => {
  const minQ = resolveStoreCartItemMinQuant(item);
  const apiQuantity = Number(item.quantity);
  const hasPersistedQuantity = Number.isFinite(apiQuantity) && apiQuantity > 0;
  const baseUnitPrice = getStoreCartBaseUnitPrice(
    item.priceList,
    item.apparelPrice || item.price,
    item.apparelBrand
  );
  const savedApiPrice = Number(item.price) || 0;
  const normalizedQuantity = hasPersistedQuantity
    ? normalizeCartLineQuantity(apiQuantity, minQ)
    : minQ;
  // Quoter-only unit prices for the store cart drawer. Never seed mock tier multipliers,
  // stale catalog/API unit prices, or a lower-tier applicable breakpoint for MOQ floors
  // (e.g. qty 16 must not inherit the qty 12 price). Prefer an exact quoter breakpoint;
  // otherwise leave 0 so the drawer quotes and writes the live unit price.
  const priceFromPriceList = getStoreCartPriceFromPriceListOnly(minQ, item.priceList, {
    exactQuantity: true,
  });
  const resolvedPrice =
    priceFromPriceList != null && priceFromPriceList > 0 ? priceFromPriceList : 0;
  let resolvedStrikedPricePerUnit = 0;

  if (item.strikedPricePerUnit && item.strikedPricePerUnit > 0) {
    resolvedStrikedPricePerUnit = item.strikedPricePerUnit;
  } else if (
    savedApiPrice > 0 &&
    resolvedPrice > 0 &&
    savedApiPrice !== resolvedPrice &&
    normalizedQuantity === minQ
  ) {
    resolvedStrikedPricePerUnit = savedApiPrice;
  }

  return {
    ...mapRegularCartFormItem(item, userOrg),
    isStore: true,
    price: resolvedPrice,
    baseUnitPrice,
    strikedPricePerUnit: resolvedStrikedPricePerUnit,
    minQuant: minQ,
    individualMoq: item.isMadeToOrder ? minQ : undefined,
    quantity: normalizedQuantity,
    priceList: item.priceList ?? [],
    // Treat a positive cart-item quantity as an explicit saved minimum so MOQ
    // hydration does not snap back to the first breakpoint after the user updates.
    savedMinimumQuantity:
      item.savedMinimumQuantity ?? (hasPersistedQuantity ? apiQuantity : undefined),
  };
};

export const getCartForm = (
  data: ICartItemDetail[],
  userOrg?: string,
  userId?: number | string
): ICartItemDetail[] =>
  data.map((item) =>
    item.isStore
      ? mapStoreCartFormItem(item, userOrg)
      : mapRegularCartFormItem(item, userOrg, userId)
  );

/** Shallow-clone cart form rows so react-hook-form can mutate fields safely */
export const cloneCartFormItems = (items: ICartItemDetail[]): ICartItemDetail[] =>
  items.map((item) => ({ ...item }));

export const getRegularCartItems = (items: ICartItemDetail[]): ICartItemDetail[] =>
  items.filter((item) => !item.isStore);

/** It synchronizes the API data of shiptypes to give the finalized shiptype records
 *  @param defShipTypes - Default ship types data that is configured in frontend.
 *  @param payLoadShipTypes - Shiptypes data from API payload.
 *
 */
export const currentShipTypes = (defShipTypes: ShipType[], payLoadShipTypes: ShipType[]) =>
  payLoadShipTypes?.map((shipType: ShipType) => {
    const shippingMethod = defShipTypes.find(
      (method: ShipType) => method.shipTypeKey === shipType.shipTypeKey
    );
    if (shippingMethod) {
      return { ...shippingMethod, ...shipType };
    }
    return shipType;
  });

/** To get the name for the Personalised custom tag */
export const getPersonalisedInfoTag = (hasCustomNames: boolean, hasCustomNumbers: boolean) => {
  let tag = '';

  if (hasCustomNames && hasCustomNumbers) {
    tag = 'Personalized Names & Numbers';
  } else if (hasCustomNames) {
    tag = 'Personalized Names';
  } else if (hasCustomNumbers) {
    tag = 'Personalized Numbers';
  }

  return tag;
};

/** This function calculates the required quant to get the price down for next breakpoint.
 * @param currentQuantity - Current quantity of the cart item.
 * @param priceList - price breakpoints with quantity and price.
 */
export const calculateRequiredQuantity = (
  currentQuantity: number,
  priceList: IQuoteBreakpoints[]
): { reqQuant: number; nextBreakPointPrice: number; currentUnitPrice: number } => {
  if (!Array.isArray(priceList) || priceList.length === 0) {
    return {
      reqQuant: 0,
      nextBreakPointPrice: 0,
      currentUnitPrice: 0,
    };
  }

  const nextBreakpointIndex = priceList.findIndex((item) => item.quantity > currentQuantity);

  if (nextBreakpointIndex !== -1) {
    const nextBreakpoint = priceList[nextBreakpointIndex];
    const currentBreakpoint = nextBreakpointIndex > 0 ? priceList[nextBreakpointIndex - 1] : null;

    const requiredQuantity = nextBreakpoint.quantity - currentQuantity;
    return {
      reqQuant: Math.max(0, requiredQuantity),
      nextBreakPointPrice: nextBreakpoint.finalPricePerUnit,
      currentUnitPrice: currentBreakpoint ? currentBreakpoint.finalPricePerUnit : 0,
    };
  }

  /** If there's no next breakpoint, the current quantity is equal or greater than the last breakpoint */
  const lastBreakpoint = priceList[priceList.length - 1];

  return {
    reqQuant: 0,
    nextBreakPointPrice: lastBreakpoint?.finalPricePerUnit,
    currentUnitPrice: lastBreakpoint?.finalPricePerUnit,
  };
};

/**
 * Method to check if the design is addable to cart
 *
 * @param design - The design.
 */
export const isDesignAddableToCart = (design: IDesignProofItem) => {
  const isDesignCanSupportPricing =
    design.isStreamlinedProofItem ||
    (design.design?.from_design_tool &&
      checkDTPricingCompatible(
        design?.printTypes ?? '',
        design.design?.print_type_info ? JSON.parse(design.design?.print_type_info) : []
      ));
  const isDesignLicenseDisabled = LICENSING_LABEL_DISABLE_CART.includes(
    design?.licensingInfo?.label ? design.licensingInfo.label : ''
  );
  const isBlockSelfCheckout = isDesignBlockedForCheckout(design);
  const showPricingSection = (design.status === 4 &&
    isDesignCanSupportPricing &&
    !isBlockSelfCheckout &&
    !isDesignLicenseDisabled) as boolean;

  return showPricingSection;
};

/**
 * Method to track the mixpanel event for the cart overlay.
 *
 * @param cartData - The cart data from the API.
 */
export const trackCartEvent = (cartData: ICartInformation) => {
  if (cartData?.cartItems?.length) {
    const proofItemIds = cartData.cartItems
      .map((item: ICartItemDetail) => item.alphaNumericProofItemId)
      .join(';');
    const versionNames = cartData.cartItems
      .map((item: ICartItemDetail) => item.proofItemVersionName)
      .join(';');
    const productIds = cartData.cartItems
      .map((item: ICartItemDetail) => item.apparelStyleCode)
      .join(';');
    const productBrands = cartData.cartItems
      .map((item: ICartItemDetail) => item.apparelBrand)
      .join(';');
    const productColors = cartData.cartItems
      .map((item: ICartItemDetail) => item.apparelHex)
      .join(';');
    const designIds = cartData.cartItems.map((item: ICartItemDetail) => item.designId).join(';');
    const proofIds = cartData.cartItems.map((item: ICartItemDetail) => item.proofId).join(';');
    const proofItemIdsList = cartData.cartItems
      .map((item: ICartItemDetail) => item.proofItemId)
      .join(';');
    captureCartViewed(
      proofItemIds,
      cartData.cartItems.length,
      productIds,
      productBrands,
      productColors,
      versionNames,
      designIds,
      proofIds,
      proofItemIdsList
    );
  } else {
    captureCartViewed(null, 0, '', '', '');
  }
};

/**
 * Helper function to validate individual MTO items in a design group
 * @param groupItems - Array of cart items in the same design group
 * @returns True if all MTO items meet their individual MOQ requirements
 */
export const validateIndividualMtoItems = (groupItems: ICartItemDetail[]): boolean =>
  groupItems.every((item) => {
    if (item.isMadeToOrder) {
      // For MTO items, check if individual quantity meets the item's individual MOQ
      const individualMoq = item.individualMoq || item.minQuant || 50;
      return (item.quantity || 0) >= individualMoq;
    }
    return true; // Non-MTO items don't have individual MOQ requirements
  });

/**
 * Checks whether the user can proceed to checkout based on minimum quantity
 *
 * This function implements dual validation for design groups:
 * 1. Design Group Total Validation: Total quantity across all items ≥ Max MOQ
 * 2. Individual MTO Item Validation: Each MTO item must meet its individual MOQ requirement
 *
 * Both conditions must be met (AND logic) for checkout to proceed.
 *
 * @param carItemDetails - Cart item details
 */
export const canProceedCheckout = (carItemDetails: ICartItemDetail[]) => {
  if (!carItemDetails?.length) {
    return true;
  }

  // Group items by design group. Use string keys consistently: object keys from
  // `undefined` coerced values (e.g. "undefined") break `parseInt` lookups.
  const designGroups = carItemDetails.reduce((acc, item, index) => {
    const rawGroupId =
      item.designGroupItemId ?? item.proofItemId ?? item.designId ?? item.id ?? item.cartItemId;
    const key = rawGroupId != null ? String(rawGroupId) : `ungrouped-${index}`;
    if (!acc[key]) {
      acc[key] = [];
    }
    acc[key].push(item);
    return acc;
  }, {} as Record<string, ICartItemDetail[]>);

  return Object.entries(designGroups).every(([, groupItems]) => {
    if (!groupItems?.length) {
      return true;
    }

    const totalGroupQuantity = groupItems.reduce(
      (sum: number, item: ICartItemDetail) => sum + (item.quantity || 0),
      0
    );

    const groupMoq = groupItems[0]?.minQuant || 0;

    const hasValidPrices = groupItems.every((item: ICartItemDetail) => item.price > 0);

    const individualMtoValidation = validateIndividualMtoItems(groupItems);

    const hasSizeStockQuantityIssue = groupItems.some(
      (item: ICartItemDetail) => item.hasSizeStockQuantityIssue === true
    );

    return (
      totalGroupQuantity >= groupMoq &&
      hasValidPrices &&
      individualMtoValidation &&
      !hasSizeStockQuantityIssue
    );
  });
};

/**
 * True when any cart item requiring custom names/numbers (`hasCustomNames`/`hasCustomNumbers`)
 * has no CSV uploaded yet. CSV upload state lives in the shared `customItem` form map
 * (keyed by designId, populated by CartCustomizeOrderDetails / CustomizeOrderItemDetails and
 * mirrored to the `csvFileForm` localStorage/cookie), not on the cart item itself — DT cart
 * parity for blocking checkout until required personalization data is provided.
 * @param cartItemDetails - Cart item details
 * @param customItem - Shared `customItem` form value (designId -> { fileName, s3Path })
 */
export const hasMissingCustomizationCsv = (
  cartItemDetails: ICartItemDetail[],
  customItem: ICartForm['customItem']
): boolean => {
  if (!cartItemDetails?.length) {
    return false;
  }

  return cartItemDetails.some((item) => {
    if (!item.hasCustomNames && !item.hasCustomNumbers) {
      return false;
    }

    // Require BOTH fields — HQ's own upload flow sets fileName synchronously then s3Path
    // asynchronously afterward (CustomizeOrderItemDetails), so a fileName-only entry means the
    // S3 upload is still in flight or failed; it must not be treated as a completed upload.
    const entry = customItem?.[item.designId];
    return !entry?.fileName || !entry?.s3Path;
  });
};

/**
 * Checks whether the user can proceed to checkout based on price
 * @param carItemDetails - Cart item details
 */
export const isCartHasZeroPriceItem = (carItemDetails: ICartItemDetail[]) => {
  const validated = carItemDetails.find((item) => (item?.price ?? 0) <= 0);
  return !!validated;
};

/**
 * Method to check whether the user can proceed to checkout based on the minimum quantity and return the error message.
 *
 * @param isDesignLicenseDisabled - Check if design license is disabled.
 * @param isCartAvailable - Is the cart or store available to click.
 * @param quantityErr - Check if quantity error.
 */
export const getCartTooltip = (
  isDesignLicenseDisabled: boolean,
  isCartAvailable: boolean,
  quantityErr: boolean,
  isCart: boolean,
  minQuantity: number
) => {
  if (isDesignLicenseDisabled || !isCartAvailable) {
    return isCart ? DISABLED_CART_TOOLTIP_LICENSING : DISABLED_STORE_TOOLTIP_LICENSING;
  }

  if (quantityErr) {
    return `The minimum order qty. for this is item is ${minQuantity}. Bump it up to add to cart.`;
  }

  return isCart ? CartButtonToolTip() : StoreButtonToolTip();
};

/**
 * Helper function to calculate design group MOQ for a given group of cart items
 * @param groupItems - Array of cart items in the same design group
 * @param itemShipPriceObject - Object containing shipping price info for each item
 * @param shipType - Current shipping type
 * @returns The maximum MOQ for the design group
 */
export const calculateDesignGroupMoq = (
  groupItems: ICartItemDetail[],
  itemShipPriceObject: { [key: string]: IAdditionalShippingPriceRes[] },
  shipType: string
): number => {
  let maxMoq = 0;

  groupItems.forEach((item) => {
    const shipPriceObjects = itemShipPriceObject[item.id];
    if (shipPriceObjects) {
      const shipPriceObject = shipPriceObjects.find(
        (obj: IAdditionalShippingPriceRes) => obj.type === shipType
      );
      if (shipPriceObject && shipPriceObject.moq > maxMoq) {
        maxMoq = shipPriceObject.moq;
      }
    }
  });

  return maxMoq;
};

/**
 * Utility function to check if MOQ Quantity values are being satisfied.
 */
export const areQuantityStepMoqsSatisfied = (
  items: IQuantitySizeItem[],
  getTotalForItemId: (itemId: number) => number,
  isDesignGroupFlow: boolean,
  designGroupInfoByItemId: Map<number, { groupItems: ICartItemDetail[] }>
): boolean =>
  items.every((item) => {
    const total = getTotalForItemId(item.id);

    if (!isDesignGroupFlow) {
      return total >= item.minQuantity;
    }

    const designGroupInfo = designGroupInfoByItemId.get(item.id);

    if (!designGroupInfo) {
      return total >= item.minQuantity;
    }

    if (item.item?.isMadeToOrder) {
      const individualMoq = item.item?.individualMoq || item.minQuantity || MTO_MOQ;
      if (total < individualMoq) {
        return false;
      }
    }

    const groupTotal = designGroupInfo.groupItems.reduce(
      (sum, gi) => sum + getTotalForItemId(gi.id),
      0
    );
    const groupMoq = designGroupInfo.groupItems[0]?.minQuant || 0;
    return groupTotal >= groupMoq;
  });

/**
 * Helper function to get design group information for a cart item
 * @param cartItems - All cart items
 * @param itemId - ID of the specific cart item
 * @returns Object containing group key and all items in the same group
 */
export const getDesignGroupInfo = (
  cartItems: ICartItemDetail[],
  itemId: number
): { groupKey: number; groupItems: ICartItemDetail[] } | null => {
  const targetItem = cartItems.find((item) => item.id === itemId);
  if (!targetItem) return null;

  const groupKey = targetItem.designGroupItemId ?? targetItem.proofItemId;
  const groupItems = cartItems.filter(
    (item) => (item.designGroupItemId ?? item.proofItemId) === groupKey
  );

  return { groupKey, groupItems };
};

/**
 * Helper function to get the total quantity for a design group
 * @param cartItems - All cart items
 * @param groupKey - The design group key
 * @returns Total quantity for the design group
 */
export const getDesignGroupTotalQuantity = (
  cartItems: ICartItemDetail[],
  groupKey: number
): number => {
  const groupItems = cartItems.filter(
    (item) => (item.designGroupItemId ?? item.proofItemId) === groupKey
  );

  return groupItems.reduce((total, item) => total + (item.quantity || 0), 0);
};

/**
 * Helper function to check if a design group meets its MOQ requirement
 * @param cartItems - All cart items
 * @param groupKey - The design group key
 * @param groupMoq - The MOQ for the design group
 * @returns True if the group meets MOQ, false otherwise
 */
export const isDesignGroupMoqMet = (
  cartItems: ICartItemDetail[],
  groupKey: number,
  groupMoq: number
): boolean => {
  const totalQuantity = getDesignGroupTotalQuantity(cartItems, groupKey);
  return totalQuantity >= groupMoq;
};

export const getCartItemGroups = (cartItems: ICartResponse) => {
  const cartItemGroups = cartItems.reduce((acc, item) => {
    const key = item.designGroupItemId ?? item.proofItemId;
    if (!acc[key]) {
      acc[key] = [];
    }
    acc[key].push(item.id);
    return acc;
  }, {} as { [key: number]: number[] });
  return cartItemGroups;
};

/**
 * Groups cart items for display order, preserving cart order within and across groups.
 * Prefer designGroupItemId when present; otherwise proofId so same-design lines
 * (e.g. different colors/products on one proof) stay adjacent even without V4 groups.
 * Falls back to proofItemId when proofId is missing.
 */
export const getCartDesignsGroupedByDesignGroup = (
  cartItems: ICartItemDetail[]
): ICartDesignDisplayGroup[] => {
  const groupMap = new Map<number, ICartDesignDisplayGroup['items']>();
  const groupOrder: number[] = [];

  cartItems.forEach((cartDesign, formIndex) => {
    const groupKey = cartDesign.designGroupItemId ?? cartDesign.proofId ?? cartDesign.proofItemId;

    if (!groupMap.has(groupKey)) {
      groupMap.set(groupKey, []);
      groupOrder.push(groupKey);
    }

    groupMap.get(groupKey)?.push({ cartDesign, formIndex });
  });

  return groupOrder.map((groupKey) => ({
    groupKey,
    items: groupMap.get(groupKey) ?? [],
  }));
};

/**
 * Flattened cart item order matching cart / summary display grouping.
 * Used by CheckoutV4 so review list and summary match the cart drawer.
 */
export const getCartItemsOrderedForDisplay = (cartItems: ICartItemDetail[]): ICartItemDetail[] =>
  getCartDesignsGroupedByDesignGroup(cartItems).flatMap((group) =>
    group.items.map(({ cartDesign }) => cartDesign)
  );

const getMoqForSelectedShipType = (
  additionalShippingPriceObject: IAdditionalShippingPriceRes[],
  shipType: string,
  fallbackMoq?: number
): number => {
  const normalizedShipType = (shipType ?? '').toLowerCase();
  const matchedRow = additionalShippingPriceObject?.find(
    (row) => (row.type ?? '').toLowerCase() === normalizedShipType
  );

  if (matchedRow?.moq != null) {
    return matchedRow.moq;
  }

  return fallbackMoq ?? DEFAULT_MOQ;
};

/**
 * Function used in cart component to get the
 * moq data and additional shipping object array
 *
 * @param cartItems - fetched cartitems
 * @param getQuote - getQuote api mutation function
 */
export const getMoqAndShipPriceObjsCart = async (
  cartItems: ICartResponse,
  getQuote: GetQuoteMutationFunction,
  shipType: string,
  setCartDetails: UseFormSetValue<ICartForm>
) => {
  const quoterAddiPriceObj: { [key: string]: IAdditionalShippingPriceRes } = {};
  const cartItemMoqObj: { [key: string]: number } = {};
  const itemShipPriceObject: { [key: string]: IAdditionalShippingPriceRes[] } = {};
  const designGroupMoqObj: { [key: string]: number } = {};
  const updatedCartPrices: { [key: number]: number } = {};
  await Promise.all(
    cartItems.map(async (itmDetail: ICartItemDetail, i: number) => {
      try {
        const designGroupQuantity = getDesignGroupQuantity(itmDetail, cartItems);
        const payload = getCartPricingReqPayload(itmDetail, designGroupQuantity);
        const isMoreThan3Colors: boolean = hasMoreThanThreeColorsColorsQuoterPayload(payload);

        payload.apparels[0].additionalShipping.type = resolveQuotableShipType(
          shipType,
          isMoreThan3Colors
        );

        const response = await getQuote({ data: payload });

        if (response && 'data' in response) {
          const quoteBreakdown: IQuoteResponse = response.data[0];
          const effectiveShipType = shipType;
          /** Lace/MTO lines must validate against MTO + product MOQ, not the selected lane (e.g. flash MOQ 24). */
          const mtoLaneMoq = getMoqForSelectedShipType(
            quoteBreakdown.additionalShippingPriceObject,
            'mto',
            quoteBreakdown.moq
          );
          const mtoResolvedMoq = Math.max(
            quoteBreakdown.moq ?? 0,
            resolveEffectiveMtoMoq(mtoLaneMoq)
          );
          const selectedLaneMoq = getMoqForSelectedShipType(
            quoteBreakdown.additionalShippingPriceObject,
            effectiveShipType,
            quoteBreakdown.moq
          );
          const productFloorMoq = quoteBreakdown.moq ?? quoteBreakdown.productMOQ ?? 0;
          const nonMtoResolvedMoq = Math.max(selectedLaneMoq, productFloorMoq);
          const resolvedMoq = itmDetail.isMadeToOrder ? mtoResolvedMoq : nonMtoResolvedMoq;
          cartItemMoqObj[itmDetail.id] = resolvedMoq;
          itemShipPriceObject[itmDetail.id] = quoteBreakdown.additionalShippingPriceObject;

          // Store the individual MOQ for MTO items before setting minQuant
          if (itmDetail.isMadeToOrder) {
            setCartDetails(`cartDetails.${i}.individualMoq`, quoteBreakdown.moq);
          }

          setCartDetails(`cartDetails.${i}.minQuant`, quoteBreakdown.moq);
          setCartDetails(`cartDetails.${i}.shipType`, shipType);
          setCartDetails(`cartDetails.${i}.priceList`, quoteBreakdown.breakpoints || []);

          // Recalculate price based on current quantity to ensure pricing is robust
          const currentPrice = resolveQuotedCartUnitPrice(itmDetail, quoteBreakdown);
          if (currentPrice && (currentPrice !== itmDetail.price || itmDetail.price === 0)) {
            updatedCartPrices[itmDetail.id] = currentPrice;
            setCartDetails(`cartDetails.${i}.price`, currentPrice);
          }

          // Update shipping prices
          const shippingPrices: { [key: string]: number } = {};
          (quoteBreakdown.additionalShippingPriceObject ?? []).forEach((option) => {
            if (['standard', 'flash', 'expedited', 'economy'].includes(option.type)) {
              shippingPrices[option.type] = option.additionalShippingPrice;
            }
          });
          setCartDetails(`cartDetails.${i}.standardShipPrice`, shippingPrices.standard || 0);
          setCartDetails(`cartDetails.${i}.flashShipPrice`, shippingPrices.flash || 0);
          setCartDetails(`cartDetails.${i}.expeditedShipPrice`, shippingPrices.expedited || 0);
          setCartDetails(`cartDetails.${i}.economyShipPrice`, shippingPrices.economy || 0);

          (quoteBreakdown.additionalShippingPriceObject ?? []).forEach(
            (itm: IAdditionalShippingPriceRes) => {
              if (quoterAddiPriceObj[itm.type]) {
                if (quoterAddiPriceObj[itm.type].daysToBeAdded < itm.daysToBeAdded) {
                  quoterAddiPriceObj[itm.type] = itm;
                }
              } else {
                quoterAddiPriceObj[itm.type] = itm;
              }
            }
          );
        }
      } catch (err) {
        console.error(err);
      }
    })
  );

  const cartItemGroups = getCartItemGroups(cartItems);

  // Calculate design group MOQ for each group
  Object.keys(cartItemGroups).forEach((groupKey) => {
    const cartItemIds = cartItemGroups[parseInt(groupKey, 10)];
    let maxMoq = 0;

    // Find the maximum MOQ for the current ship type among all items in the group
    cartItemIds.forEach((cartItemId) => {
      const shipPriceObjects = itemShipPriceObject[cartItemId];
      if (shipPriceObjects) {
        const shipPriceObject = shipPriceObjects.find(
          (obj: IAdditionalShippingPriceRes) => obj.type === shipType
        );
        if (shipPriceObject && shipPriceObject.moq > maxMoq) {
          maxMoq = shipPriceObject.moq;
        }
      }
    });

    // Set the design group MOQ
    designGroupMoqObj[groupKey] = maxMoq;

    // Update minQuant for all items in this design group to the group MOQ
    // BUT preserve individual MOQ for MTO items for individual validation
    cartItemIds.forEach((cartItemId) => {
      const itemIndex = cartItems.findIndex((item) => item.id === cartItemId);
      if (itemIndex !== -1) {
        const currentItem = cartItems[itemIndex];

        // Store the individual MOQ for MTO items before overwriting minQuant
        if (currentItem.isMadeToOrder) {
          setCartDetails(`cartDetails.${itemIndex}.individualMoq`, currentItem.minQuant || 50);
        }

        // Set the group MOQ for all items (this is used for total validation)
        setCartDetails(`cartDetails.${itemIndex}.minQuant`, maxMoq);
        cartItemMoqObj[cartItemId] = maxMoq;
      }
    });
  });

  return {
    cartItemMoqObj,
    quoterAddiPriceObj,
    itemShipPriceObject,
    designGroupMoqObj,
    updatedCartPrices,
  };
};

/**
 * Function to set the shiptype on cart
 * when cart item is removed
 * Please keep in sync with backend logic
 *
 * @param existingItems - existing cartitems
 * @param setCartDetails - setValue from react hook form
 */
export const setShipTypeOnCartItemRmv = (
  existingItems: ICartItemDetail[],
  setCartDetails: UseFormSetValue<ICartForm>
) => {
  if (!existingItems.length) return;

  const existingShipType = existingItems[0].shipType;
  const activeMtoCartItem = existingItems.find((itm: ICartItemDetail) => itm.isMadeToOrder);
  let toBeShipType = existingItems[0].shipType;

  if (!activeMtoCartItem) {
    if (existingShipType === 'mto') {
      toBeShipType = 'standard';
    } else if (existingShipType === 'flash') {
      const isNotFlashEligibleItem = existingItems.find((itm) => !itm.isFlashEligible);

      if (isNotFlashEligibleItem) {
        toBeShipType = 'standard';
      }
    } else if (existingShipType === 'expedited') {
      toBeShipType = 'expedited';
    }
    setCartDetails('shipTypeKey', toBeShipType);

    existingItems.forEach((_, i) => {
      setCartDetails(`cartDetails.${i}.shipType`, toBeShipType);
    });
  }
};

/**
 * Function to get cart item ID for a given design ID.
 * @param {ICartResponse} cartItems - Fetched cart items.
 * @param {number} designId - The design ID to search for.
 * @returns {number | null} - The cart item ID or null if not found.
 */
export const getCartItemId = (cartItems: ICartResponse, designId: number): number | null => {
  try {
    const foundItem = cartItems.find(
      (item: ICartItemDetail) => item.designPricing.design_id === designId
    );
    return foundItem ? foundItem.id : null;
  } catch {
    return null;
  }
};

/**
 * Resolves the design id for a proof item shown on design cards.
 */
export interface IStoreCartDesignIdSource {
  designId?: number;
  designLicensing?: { design_id?: number };
  designLicenses?: { design_id?: number };
}

export interface IStoreCartDesignIdLookup extends IStoreCartDesignIdSource {
  id: string;
}

export const resolveDesignProofItemDesignId = (
  designItem: IStoreCartDesignIdSource
): number | undefined => {
  if (typeof designItem.designId === 'number' && designItem.designId > 0) {
    return designItem.designId;
  }

  const licensingDesignId =
    designItem.designLicensing?.design_id ?? designItem.designLicenses?.design_id;

  if (typeof licensingDesignId === 'number' && licensingDesignId > 0) {
    return licensingDesignId;
  }

  return undefined;
};

/**
 * Checks whether a design card proof item is already in the store cart.
 */
export const isDesignInStoreCart = (
  designItem: IStoreCartDesignIdLookup,
  storeCountObject: ICartCountResponse,
  storeItems: ICartResponse = []
): boolean => {
  const storeDesignIds = storeCountObject?.storeDesignIds ?? [];
  const resolvedDesignId = resolveDesignProofItemDesignId(designItem);

  if (resolvedDesignId && storeDesignIds.includes(resolvedDesignId)) {
    return true;
  }

  const proofItemId = parseInt(designItem.id, 10);

  if (Number.isNaN(proofItemId) || !storeItems?.length) {
    return false;
  }

  return storeItems.some(
    (item) => item.proofItemId === proofItemId || item.designGroupItemId === proofItemId
  );
};

const getDesignItemColorDesignIds = (designItem: IDesignItem): number[] => {
  const designIds = new Set<number>();
  const transformedDesigns = designItem.designGroupDesigns?.transformedDesigns ?? [];

  transformedDesigns.forEach((product) => {
    product.colors.forEach((color) => {
      if (color.designId > 0) {
        designIds.add(color.designId);
      }
    });
  });

  const designObj = designItem.design as IDesignObj | undefined;
  const designObjId = designObj?.id;

  if (typeof designObjId === 'number' && designObjId > 0) {
    designIds.add(designObjId);
  }

  const licensingDesignId =
    designItem.designLicensing?.design_id ??
    (designObj?.designLicensing as { design_id?: number } | undefined)?.design_id;

  if (typeof licensingDesignId === 'number' && licensingDesignId > 0) {
    designIds.add(licensingDesignId);
  }

  return Array.from(designIds);
};

/** Merges store count data from cart and store-cart slices (API may populate either). */
export const mergeStoreCountObjects = (
  primary: ICartCountResponse,
  secondary: ICartCountResponse
): ICartCountResponse => ({
  ...primary,
  storeCount: primary.storeCount || secondary.storeCount || 0,
  storeDesignIds: [
    ...new Set([...(primary.storeDesignIds ?? []), ...(secondary.storeDesignIds ?? [])]),
  ],
});

const normalizeAlphaNumericProofItemId = (value?: string) => value?.trim().toUpperCase() ?? '';

const doesStoreItemMatchDesignItem = (
  item: ICartItemDetail,
  designItem: IDesignItem,
  groupDesignIds: number[]
): boolean => {
  const alphaNumericProofItemId = normalizeAlphaNumericProofItemId(designItem.proof_item_id);
  const storeAlphaNumericProofItemId = normalizeAlphaNumericProofItemId(
    item.alphaNumericProofItemId
  );

  if (
    alphaNumericProofItemId &&
    storeAlphaNumericProofItemId &&
    alphaNumericProofItemId === storeAlphaNumericProofItemId
  ) {
    return true;
  }

  const proofItemId = Number(designItem.id);

  if (
    !Number.isNaN(proofItemId) &&
    (item.proofItemId === proofItemId || item.designGroupItemId === proofItemId)
  ) {
    return true;
  }

  const storeItemDesignIds = [item.designId, item.designPricing?.design_id].filter(
    (designId): designId is number => typeof designId === 'number' && designId > 0
  );

  return storeItemDesignIds.some((designId) => groupDesignIds.includes(designId));
};

/**
 * Checks whether a design details page proof item is already in the store cart.
 * Supports single designs and design-group proofs (any matching color/product line counts).
 */
export const isDesignItemInStoreCart = (
  designItem: IDesignItem,
  storeCountObject: ICartCountResponse,
  storeItems: ICartResponse = []
): boolean => {
  const storeDesignIds = storeCountObject?.storeDesignIds ?? [];
  const groupDesignIds = getDesignItemColorDesignIds(designItem);

  if (groupDesignIds.some((designId) => storeDesignIds.includes(designId))) {
    return true;
  }

  if (storeItems.length > 0) {
    const hasMatchingStoreItem = storeItems.some((item) =>
      doesStoreItemMatchDesignItem(item, designItem, groupDesignIds)
    );

    if (hasMatchingStoreItem) {
      return true;
    }
  }

  return isDesignInStoreCart(
    {
      id: String(designItem.id),
      designId: groupDesignIds[0],
      designLicensing: designItem.designLicensing ?? undefined,
    },
    storeCountObject,
    storeItems
  );
};
